import { createSlice } from '@reduxjs/toolkit';
import i18n from '../../../i18n';

const initialState = {
  vehicles: [],
  units: [],
  vehicleTypes: [],
  total: 0,
  page: 1,
  pageSize: 10,
  sortColumn: 'licensePlate',
  sortDirection: 'asc',
  filterName: '',
  loading: false,
  error: null,
  successMessage: null,
  selectedVehicle: null,
};

const vehiclesSlice = createSlice({
  name: 'vehicles',
  initialState,
  reducers: {
    // Action to trigger fetch init data (saga will handle this)
    fetchInit: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchInitSuccess: (state, action) => {
      state.loading = false;
      state.units = action.payload.units || [];
      state.vehicleTypes = action.payload.vehicleTypes || [];
      state.error = null;
    },
    fetchInitFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger fetch vehicles (saga will handle this)
    fetchVehicles: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchVehiclesSuccess: (state, action) => {
      state.loading = false;
      state.vehicles = action.payload.data || [];
      // Extract pagination info from meta object
      state.total = action.payload.meta?.total || 0;
      state.page = action.payload.meta?.page || 1;
      state.pageSize = action.payload.meta?.size || 10;
      state.error = null;
    },
    fetchVehiclesFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger add vehicle
    addVehicle: (state) => {
      state.loading = true;
      state.error = null;
    },
    addVehicleSuccess: (state, action) => {
      state.loading = false;
      state.vehicles.push(action.payload);
      state.error = null;
      state.successMessage = i18n.t('vehicles.success.created');
    },
    addVehicleFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger fetch single vehicle
    fetchVehicleById: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchVehicleByIdSuccess: (state, action) => {
      state.loading = false;
      state.selectedVehicle = action.payload;
      state.error = null;
    },
    fetchVehicleByIdFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger update vehicle
    updateVehicle: (state) => {
      state.loading = true;
      state.error = null;
    },
    updateVehicleSuccess: (state, action) => {
      state.loading = false;
      const index = state.vehicles.findIndex((vehicle) => vehicle.id === action.payload.id);
      if (index !== -1) {
        state.vehicles[index] = action.payload;
      }
      state.error = null;
      state.successMessage = i18n.t('vehicles.success.updated');
    },
    updateVehicleFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger delete vehicle
    deleteVehicle: (state) => {
      state.loading = true;
      state.error = null;
    },
    deleteVehicleSuccess: (state, action) => {
      state.loading = false;
      state.vehicles = state.vehicles.filter((vehicle) => vehicle.id !== action.payload);
      state.error = null;
      state.successMessage = i18n.t('vehicles.success.deleted');
    },
    deleteVehicleFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger bulk delete vehicles
    bulkDeleteVehicles: (state) => {
      state.loading = true;
      state.error = null;
    },
    bulkDeleteVehiclesSuccess: (state, action) => {
      state.loading = false;
      state.vehicles = state.vehicles.filter((vehicle) => !action.payload.includes(vehicle.id));
      state.error = null;
      state.successMessage = i18n.t('vehicles.success.bulkDeleted', { count: action.payload.length });
    },
    bulkDeleteVehiclesFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Set selected vehicle
    setSelectedVehicle: (state, action) => {
      state.selectedVehicle = action.payload;
    },
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    // Clear success message
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    // Set pagination
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
      state.page = 1; // Reset to first page when page size changes
    },
    // Set sort
    setSortColumn: (state, action) => {
      state.sortColumn = action.payload;
    },
    setSortDirection: (state, action) => {
      state.sortDirection = action.payload;
    },
    // Set filter
    setFilterName: (state, action) => {
      state.filterName = action.payload;
      state.page = 1; // Reset to first page when filter changes
    },
  },
});

// Export actions
export const {
  fetchInit,
  fetchInitSuccess,
  fetchInitFailure,
  fetchVehicles,
  fetchVehiclesSuccess,
  fetchVehiclesFailure,
  fetchVehicleById,
  fetchVehicleByIdSuccess,
  fetchVehicleByIdFailure,
  addVehicle,
  addVehicleSuccess,
  addVehicleFailure,
  updateVehicle,
  updateVehicleSuccess,
  updateVehicleFailure,
  deleteVehicle,
  deleteVehicleSuccess,
  deleteVehicleFailure,
  bulkDeleteVehicles,
  bulkDeleteVehiclesSuccess,
  bulkDeleteVehiclesFailure,
  setSelectedVehicle,
  clearError,
  clearSuccessMessage,
  setPage,
  setPageSize,
  setSortColumn,
  setSortDirection,
  setFilterName,
} = vehiclesSlice.actions;

// Selectors
export const selectVehicles = (state) => state.vehicles.vehicles;
export const selectUnits = (state) => state.vehicles.units;
export const selectVehicleTypesForVehicles = (state) => state.vehicles.vehicleTypes;
export const selectVehiclesLoading = (state) => state.vehicles.loading;
export const selectVehiclesError = (state) => state.vehicles.error;
export const selectVehiclesSuccessMessage = (state) => state.vehicles.successMessage;
export const selectSelectedVehicle = (state) => state.vehicles.selectedVehicle;
export const selectVehiclesTotal = (state) => state.vehicles.total;
export const selectVehiclesPage = (state) => state.vehicles.page;
export const selectVehiclesPageSize = (state) => state.vehicles.pageSize;
export const selectVehiclesSortColumn = (state) => state.vehicles.sortColumn;
export const selectVehiclesSortDirection = (state) => state.vehicles.sortDirection;
export const selectVehiclesFilterName = (state) => state.vehicles.filterName;

// Export reducer
export default vehiclesSlice.reducer;
